import React, { useEffect, useState } from 'react';

import { makeMuiStyles, useCommonStyles } from '@styles/index';
import clsx from 'clsx';
import Color from 'color';
import { useHistory } from 'react-router-dom';

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    position: 'relative',
  },
  percent: {
    ...common.flexCenter,
    position: 'relative',
    top: '15px',
    width: '125px',
    height: '85px',
  },
  svg: {
    position: 'relative',
    width: '113px',
    height: '113px',
    rotate: '162deg',
  },
  circle: {
    width: '100%',
    height: '100%',
    fill: 'none',
    strokeWidth: 10,
    strokeLinecap: 'round',
    transform: 'translate(5px, 5px)',
    strokeDasharray: 315,
    strokeDashoffset: 'calc(315px - (315px * 0.6 * 100) / 100)',
    stroke: Color(theme.palette.primary.main).alpha(0.1).toString(),
    '&:nth-child(2)': {
      strokeDasharray: 315,
      strokeDashoffset: 'calc(315px - (315px * 0.8 * 60) / 100)',
      stroke: theme.palette.primary.main,
      '&$orange': {
        stroke: Color('#F08701').alpha(0.8).toString(),
      },
    },
  },
  textContainer: {
    ...common.flexColumnCenter,
    ...common.exSmallerText,
    ...common.textGray700,
    position: 'absolute',
    top: 0,
    left: 3,
    width: '100%',
    height: '100%',
  },
  digit: {
    ...common.baseButton,
    fontWeight: 500,
    fontSize: '1.7rem',
    textDecorationLine: 'underline',
  },
  orange: {
    color: '#F08701',
  },
}));

interface CircularProgressBarProps {
  text: string;
  number: number;
  dominator: number;
  link?: string;
  color?: 'primary' | 'orange';
}

export const CircularProgressBar: React.FC<CircularProgressBarProps> = (props) => {
  const { color = 'primary', text, number, dominator, link } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const history = useHistory();
  const [animatedProgress, setAnimatedProgress] = useState(0);

  const targetProgress = Math.min(1, dominator === 0 ? 0 : number / dominator);

  useEffect(() => {
    let frame = requestAnimationFrame(step);

    let i = 0;
    let steps = 40;

    let start = animatedProgress;

    function step() {
      if (i <= steps) {
        setAnimatedProgress(
          start + (targetProgress - start) * Math.sin(((i / steps) * Math.PI) / 2),
        );
        frame = requestAnimationFrame(step);
      }

      i++;
    }

    return () => {
      cancelAnimationFrame(frame);
    };
  }, [targetProgress]);

  return (
    <div className={classes.container}>
      <div className={classes.percent}>
        <svg className={classes.svg}>
          <circle className={classes.circle} cx="50" cy="50" r="50"></circle>
          <circle
            className={clsx(classes.circle, color === 'orange' && classes.orange)}
            style={{ strokeDashoffset: `calc(315px - (315px * ${animatedProgress} * 60) / 100)` }}
            cx="50"
            cy="50"
            r="50"
          ></circle>
        </svg>
        <div className={classes.textContainer}>
          <div>{text}</div>
          <div>
            <span
              className={clsx(
                classes.digit,
                color === 'orange' && classes.orange,
                color === 'primary' && classes.textPrimary,
              )}
              onClick={() => {
                if (link) {
                  history.push(link);
                }
              }}
            >
              {number}
            </span>{' '}
            件
          </div>
        </div>
      </div>
    </div>
  );
};
